/**
 * ContactSection Component
 *
 * Displays contact information and form for showroom inquiries.
 * Three-column layout: Title | Info | Form
 */

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Input,
  Link,
  Text,
  Textarea,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Phone icon (outline style)
 */
const PhoneIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </Icon>
);

/**
 * Email icon (outline style)
 */
const EmailIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <polyline
      fill="none"
      points="22,6 12,13 2,6"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </Icon>
);

/**
 * Person icon (outline style)
 */
const PersonIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <circle
      cx="12"
      cy="8"
      fill="none"
      r="4"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M20 21v-2c0-2.21-3.58-4-8-4s-8 1.79-8 4v2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </Icon>
);

/**
 * ContactSection component
 *
 * @param {Object} props - Component props
 * @param {Object} props.store - SFCC Store data
 * @param {Object} props.contactFormDescription - Rich text description from Contentful
 * @returns {React.ReactElement}
 */
export const ContactSection = ({
  contactFormDescription,
  store,
  ...props
}) => {
  const {formatMessage} = useIntl();
  const styles = useMultiStyleConfig('ContactSection', {});

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const {name, value} = e.target;

    setFormData((prev) => ({...prev, [name]: value}));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement form submission logic
    console.log('Form submitted:', formData);
  };

  // Get manager name from store custom attribute
  const managerName = store?.showroomManager || store?.c_showroomManager;

  // Extract plain text from rich text JSON if available
  const descriptionText = contactFormDescription?.json?.content
    ?.map((node) => node.content?.map((c) => c.value).join(''))
    .join(' ') || '';

  return (
    <Box {...styles.container} {...props}>
      <Grid {...styles.grid}>
        {/* Column 1: Title */}
        <GridItem {...styles.titleColumn}>
          <Heading as="h2" {...styles.title}>
            {formatMessage({
              defaultMessage: 'Contact showroom',
              id: 'contact_section.title',
            })}
          </Heading>
        </GridItem>

        {/* Column 2: Contact Info */}
        <GridItem {...styles.infoColumn}>
          <VStack {...styles.infoStack}>
            {/* Phone */}
            {store?.phone && (
              <HStack {...styles.contactItem}>
                <PhoneIcon {...styles.icon} />
                <Link
                  {...styles.link}
                  href={`tel:${store.phone}`}
                >
                  {store.phone}
                </Link>
              </HStack>
            )}

            {/* Email */}
            {store?.email && (
              <HStack {...styles.contactItem}>
                <EmailIcon {...styles.icon} />
                <Link
                  {...styles.link}
                  href={`mailto:${store.email}`}
                >
                  {store.email}
                </Link>
              </HStack>
            )}

            {/* Showroom Manager */}
            {managerName && (
              <HStack {...styles.contactItem}>
                <PersonIcon {...styles.icon} />
                <Text {...styles.text}>
                  {managerName} - {formatMessage({
                    defaultMessage: 'Showroom manager',
                    id: 'contact_section.showroom_manager',
                  })}
                </Text>
              </HStack>
            )}

            {/* Description */}
            {descriptionText && (
              <Text {...styles.description}>
                {descriptionText}
              </Text>
            )}
          </VStack>
        </GridItem>

        {/* Column 3: Contact Form */}
        <GridItem {...styles.formColumn}>
          <form onSubmit={handleSubmit} {...styles.form}>
            <VStack {...styles.formStack}>
              <Input
                {...styles.input}
                name="firstName"
                value={formData.firstName}
                placeholder={formatMessage({
                  defaultMessage: 'First name',
                  id: 'contact_section.first_name_placeholder',
                })}
                onChange={handleInputChange}
              />
              <Input
                {...styles.input}
                name="lastName"
                value={formData.lastName}
                placeholder={formatMessage({
                  defaultMessage: 'Last name',
                  id: 'contact_section.last_name_placeholder',
                })}
                onChange={handleInputChange}
              />
              <Input
                {...styles.input}
                name="email"
                type="email"
                value={formData.email}
                placeholder={formatMessage({
                  defaultMessage: 'Email address',
                  id: 'contact_section.email_placeholder',
                })}
                onChange={handleInputChange}
              />
              <Textarea
                {...styles.textarea}
                name="message"
                value={formData.message}
                placeholder={formatMessage({
                  defaultMessage: 'Message',
                  id: 'contact_section.message_placeholder',
                })}
                onChange={handleInputChange}
              />
              <Button
                {...styles.submitButton}
                type="submit"
              >
                {formatMessage({
                  defaultMessage: 'Send enquiry',
                  id: 'contact_section.send_enquiry',
                })}
              </Button>
            </VStack>
          </form>
        </GridItem>
      </Grid>
    </Box>
  );
};

ContactSection.displayName = 'ContactSection';

ContactSection.propTypes = {
  contactFormDescription: PropTypes.shape({
    json: PropTypes.object,
  }),
  store: PropTypes.shape({
    c_showroomManager: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    showroomManager: PropTypes.string,
  }),
};

export default ContactSection;
